import { Button, Card, Group, Stack, Text } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { formatCompanyProfileTextForDisplay } from "@/shared/utils/capitalizeLeadingCharacter";
import {
  buildCompanyDiscoverPath,
  companyProfileAriaLabel,
} from "@/shared/utils/companyProfileLink.util";

export type SarahChatCompanyCard = {
  companyId: string;
  gtid: string;
  subDescription: string | null;
  customerOfferHighlight: string | null;
  industryCategory: string[];
  headquartersCountry: string;
  headquartersCity: string;
};

const VIEW_BTN_STYLES = {
  root: {
    borderColor: "#04305c",
    color: "#04305c",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#F8FAFC",
      borderColor: "#04305c",
    },
  },
} as const;

function oneLineOffer(company: SarahChatCompanyCard): string {
  const raw =
    company.subDescription?.trim() || company.customerOfferHighlight?.trim() || "";
  return formatCompanyProfileTextForDisplay(raw) || "Verified company on Discover";
}

export default function SarahCompanyMatchCard({ company }: { company: SarahChatCompanyCard }) {
  const gtid = company.gtid?.trim() || "N/A";
  const flag = flagEmojiFromHeadquartersCountry(company.headquartersCountry);
  const country = company.headquartersCountry?.trim() || "Location not listed";
  const profilePath = buildCompanyDiscoverPath(company.companyId);

  return (
    <Card
      radius="md"
      p="xs"
      withBorder
      style={{
        border: "1px solid #E2E8F0",
        background: "#FFFFFF",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Stack gap={6}>
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap={8}>
          <Text fw={700} size="xs" c="#04305c" lh={1.2} lineClamp={1} style={{ minWidth: 0 }}>
            {gtid}
          </Text>
          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
            {flag ? (
              <Text
                component="span"
                fz={14}
                lh={1}
                role="img"
                aria-label={`${country} flag`}
              >
                {flag}
              </Text>
            ) : null}
            <Text size="xs" c="#475569" lh={1.2} lineClamp={1} maw={110}>
              {country}
            </Text>
          </Group>
        </Group>
        <Text size="xs" c="#334155" lh={1.35} lineClamp={2} style={{ fontSize: 11 }}>
          {oneLineOffer(company)}
        </Text>
        <Button
          component={Link}
          to={profilePath}
          variant="outline"
          fullWidth
          radius="md"
          size="compact-xs"
          styles={VIEW_BTN_STYLES}
          rightSection={<IconArrowRight size={12} />}
          aria-label={companyProfileAriaLabel(company.gtid, gtid)}
        >
          View
        </Button>
      </Stack>
    </Card>
  );
}
